import React, { useCallback, useMemo, useState } from "react";
import DeleteConfirmModal from "../components/Models/DeleteConfirmModal";

/**
 * Opens a shared delete confirmation modal before running onConfirm.
 *
 * @param {{ title?: string, message?: string, confirmLabel?: string }} [opts]
 * @returns {{ confirmDelete: (opts: { name?: string, onConfirm: () => void }) => void, deleteConfirmModal: React.ReactNode }}
 */
export function useDeleteConfirm({
  title = "Delete item?",
  message = "This action cannot be undone.",
  confirmLabel = "Delete",
} = {}) {
  const [pending, setPending] = useState(null);

  const confirmDelete = useCallback((opts) => {
    if (!opts?.onConfirm) return;
    setPending(opts);
  }, []);

  const close = useCallback(() => setPending(null), []);

  const handleConfirm = useCallback(() => {
    const fn = pending?.onConfirm;
    setPending(null);
    fn?.();
  }, [pending]);

  const deleteConfirmModal = useMemo(
    () => (
      <DeleteConfirmModal
        isOpen={Boolean(pending)}
        onClose={close}
        onConfirm={handleConfirm}
        title={title}
        itemName={pending?.name}
        message={message}
        confirmLabel={confirmLabel}
      />
    ),
    [pending, close, handleConfirm, title, message, confirmLabel]
  );

  return { confirmDelete, deleteConfirmModal };
}
